import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { commentSchema } from '../../../forms/schemas/comment'
import { Button, ButtonVariant } from '../../common/Button'
import { TextArea } from '../../common/TextArea'
import { IconChoices } from '../../common/Icon'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { ToastType } from '../../common/Toast/toast-type'
import { createComment, discussionQueryKey } from '../../../api/comments'
import { RoleOptionsDropdown } from '../RoleDropdown'
import { CommentComposition } from '../../../generated/api/comment-row'
import { PostRole, PostSociety } from '../../../generated/api/post-row'
import { commentedRow, refreshPostRow, updatePostRow } from '../postRowCache'

interface CommentInputProps {
  postId: string
  society: PostSociety
  composition: CommentComposition
  onCreated: (commentId: string) => void
}

export const CommentInput = ({
  postId,
  society,
  composition,
  onCreated,
}: CommentInputProps): JSX.Element => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(commentSchema),
    defaultValues: {
      comment: '',
      role: composition.defaultRole ?? composition.roles[0] ?? 'resident',
    },
  })

  const queryClient = useQueryClient()

  const { mutate: mutateCreateComment, isPending } = useMutation({
    mutationFn: async ({
      comment,
      role,
    }: {
      comment: string
      role?: string
    }) => {
      return (
        await createComment({
          postId,
          message: comment,
          role: (role || 'resident') as PostRole,
        })
      ).data.data
    },
    meta: { skipGlobalInvalidate: true },
    onSuccess: (created) => {
      setValue('comment', '')
      updatePostRow(queryClient, postId, (row) => commentedRow(row, 1))
      queryClient.invalidateQueries({
        queryKey: discussionQueryKey(postId),
        refetchType: created?._id ? 'none' : 'active',
      })
      if (created?._id) {
        onCreated(created._id)
      }
    },
    onError: () => {
      showErrorMessage()
    },
    onSettled: () => refreshPostRow(queryClient, postId),
  })

  const onSubmit = async (
    data: InferType<typeof commentSchema>
  ): Promise<void> => {
    mutateCreateComment({ comment: data.comment, role: data.role })
  }

  const onError = (): void => {
    showErrorMessage()
  }

  return (
    <div className="mt-2 flex">
      <div className="flex flex-1 gap-2 self-end">
        <div className="flex w-full flex-col gap-2">
          <Controller
            control={control}
            name="role"
            render={({ field: { value, onChange } }) => (
              <RoleOptionsDropdown
                roles={composition.roles}
                author={composition.author}
                societyType={society.type}
                societyCoverUrl={society.coverUrl}
                value={value}
                onChange={onChange}
                showLabel={false}
              />
            )}
          />
          <Controller
            control={control}
            name="comment"
            render={({ field: { value, onChange, onBlur } }) => (
              <TextArea
                placeholder={translate('post.commentInputPlaceholder')}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                rows={1}
                fixedHeight={false}
              />
            )}
          />
        </div>
        <div className="flex self-end bg-white">
          <Button
            type="submit"
            disabled={!isValid || isPending}
            className="h-11 w-11 border-none"
            size="no-padding"
            iconSize={18}
            variant={ButtonVariant.PRIMARY}
            icon={IconChoices.SEND}
            onClick={handleSubmit(onSubmit, onError)}
          />
        </div>
      </div>
    </div>
  )
}
